import React, {useState} from 'react';
import {Button, Card, Checkbox, Col, Form, Input, InputNumber, Layout, message, Row, Select, Space, Typography} from 'antd';
import {DownOutlined} from '@ant-design/icons';
import {saveAs} from 'file-saver';
import {zongJiuService, ZongJiuExportRequest} from '../../services/domain/report/zongJiuService';

interface ExportValues extends Omit<ZongJiuExportRequest, 'isSuspended' | 'isWithdrawaled'> {
    isSuspended?: '' | 'true' | 'false';
    isWithdrawaled?: '' | 'true' | 'false';
    includeMemberMaster: boolean;
    includeProductMapping: boolean;
}

const ZongJiuExport: React.FC = () => {
    const [form] = Form.useForm<ExportValues>();
    const [loading, setLoading] = useState(false);

    const download = async (values: ExportValues) => {
        if (!values.includeMemberMaster && !values.includeProductMapping) {
            message.warning('請至少選擇一種轉檔內容');
            return;
        }
        const request: ZongJiuExportRequest = {
            memberIdStart: values.memberIdStart?.trim() || '',
            memberIdEnd: values.memberIdEnd?.trim() || '',
            membershipLevelStart: values.membershipLevelStart?.trim() || '',
            membershipLevelEnd: values.membershipLevelEnd?.trim() || '',
            delegateMaxCount: values.delegateMaxCount,
            certificationMaxCount: values.certificationMaxCount,
            isSuspended: values.isSuspended === 'true' ? true : values.isSuspended === 'false' ? false : undefined,
            isWithdrawaled: values.isWithdrawaled === 'true' ? true : values.isWithdrawaled === 'false' ? false : undefined,
        };
        for (const [start, end, label] of [
            [request.memberIdStart, request.memberIdEnd, '會員編號'],
            [request.membershipLevelStart, request.membershipLevelEnd, '會員級數'],
        ]) {
            if (!!start !== !!end || (start && end && start > end)) {
                message.warning(`${label}請填寫完整起迄值，且起值不得大於迄值`);
                return;
            }
        }
        setLoading(true);
        try {
            // 先取得所有選取的檔案，避免其中一支 API 失敗卻下載了不完整的一組。
            const files = await Promise.all([
                ...(values.includeMemberMaster ? [zongJiuService.getMemberFile(request)] : []),
                ...(values.includeProductMapping ? [zongJiuService.getProductFile(request)] : []),
            ]);
            files.forEach((file, index) => saveAs(file.blob, file.fileName === 'downloaded-file.txt'
                ? `${values.includeMemberMaster && index === 0 ? '宗九_會員主檔' : '宗九_產品對照檔'}.txt`
                : file.fileName));
            message.success(`已產生 ${files.length} 個 TXT 檔案`);
        } catch (error: any) {
            let detail = '匯出失敗，請稍後重試';
            const data = error?.response?.data;
            if (data instanceof Blob) {
                try { detail = JSON.parse(await data.text()).message || detail; } catch { /* 非 JSON 錯誤 */ }
            } else if (data?.message) {
                detail = data.message;
            }
            message.error(detail);
        } finally {
            setLoading(false);
        }
    };

    return <Layout className="member-directory-export-page">
        <Layout.Content>
            <Row justify="center" className="mb-lg">
                <Col><Typography.Title level={3} className="membership-title-with-emoji">會員資料轉宗九匯出</Typography.Title></Col>
            </Row>
            <Row justify="end" className="mb-lg">
                <Col><Button type="primary" icon={<DownOutlined/>} loading={loading} onClick={() => form.submit()}>下載</Button></Col>
            </Row>
            <Card>
                <Form form={form} layout="horizontal" onFinish={download} disabled={loading}
                    labelCol={{flex: '0 0 140px'}} wrapperCol={{flex: '1 1 0'}} requiredMark={false}
                    initialValues={{includeMemberMaster: true, includeProductMapping: true, delegateMaxCount: 5, certificationMaxCount: 20, isSuspended: 'false', isWithdrawaled: 'false'}}>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="會員證號" style={{marginBottom: 16}}>
                                <Space.Compact>
                                    <Form.Item name="memberIdStart" noStyle><Input style={{width: 120}} aria-label="會員編號起值" placeholder="起始值" onChange={e => form.setFieldsValue({memberIdEnd: e.target.value})}/></Form.Item>
                                    <Form.Item name="memberIdEnd" noStyle><Input style={{width: 120}} aria-label="會員編號迄值" placeholder="迄值"/></Form.Item>
                                </Space.Compact>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="會員級數" style={{marginBottom: 16}}>
                                <Space.Compact>
                                    <Form.Item name="membershipLevelStart" noStyle><Input style={{width: 120}} aria-label="會員級數起值" placeholder="起始值" onChange={e => form.setFieldsValue({membershipLevelEnd: e.target.value})}/></Form.Item>
                                    <Form.Item name="membershipLevelEnd" noStyle><Input style={{width: 120}} aria-label="會員級數迄值" placeholder="迄值"/></Form.Item>
                                </Space.Compact>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="是否停權" name="isSuspended" style={{marginBottom: 16}}>
                                <Select placeholder="請選擇" allowClear options={[{value: '', label: '請選擇'}, {value: 'true', label: '是'}, {value: 'false', label: '否'}]}/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="是否退會" name="isWithdrawaled" style={{marginBottom: 16}}>
                                <Select placeholder="請選擇" allowClear options={[{value: '', label: '請選擇'}, {value: 'true', label: '是'}, {value: 'false', label: '否'}]}/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}><Form.Item label="會員代表最大數量" name="delegateMaxCount" style={{marginBottom: 16}} rules={[{required: true, type: 'integer', min: 0, max: 100, message: '請輸入 0～100 的整數'}]}><InputNumber min={0} max={100} style={{width: '100%'}}/></Form.Item></Col>
                        <Col xs={24} md={12}><Form.Item label="會員認證最大數量" name="certificationMaxCount" style={{marginBottom: 16}} rules={[{required: true, type: 'integer', min: 0, max: 100, message: '請輸入 0～100 的整數'}]}><InputNumber min={0} max={100} style={{width: '100%'}}/></Form.Item></Col>
                    </Row>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="轉檔內容" style={{marginBottom: 16}}><Space.Compact>
                                <Form.Item name="includeMemberMaster" valuePropName="checked" noStyle><Checkbox>會員主檔</Checkbox></Form.Item>
                                <Form.Item name="includeProductMapping" valuePropName="checked" noStyle><Checkbox>產品對照表</Checkbox></Form.Item>
                            </Space.Compact></Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </Layout.Content>
    </Layout>;
};

export default ZongJiuExport;
